import { ref } from "vue";
import { defineStore } from "pinia";
import networkController from "@/js/networkController.js";
import apiEndpoints from "@/assets/json/apiEndpoints.json";

export const useBuyersStore = defineStore('buyersStore', () => {
  const BUYER_TOKEN = ref<string | null>(null);
  const BUYER_INFO = ref<any | null>(null);
  const SURVEY_ID = ref<string | null>(null);
  // Телефон, вводимый при авторизации покупателя (сохраняем между переходами)
  const LOGIN_PHONE = ref<string>('')
  // Флажок согласия на механики (сохраняем между переходами)
  const LOGIN_ACCEPT = ref<boolean>(false)

  const CURRENT_SELLER_ID = ref<number | null>(null)
  const CURRENT_TASK_ID = ref<number | null>(null)
  const CURRENT_TASK_DETAILS = ref<any | null>(null)

  // Добавлено: шаг авторизации покупателя ('enter' | 'code')
  const AUTH_STEP = ref<'enter' | 'code'>('enter')
  function setAuthStep(step: 'enter' | 'code') {
    AUTH_STEP.value = step
  }

  // Добавлено: текст последней ошибки для экрана "Опрос недоступен"
  const LAST_ERROR_MESSAGE = ref<string>('')
  function setLastErrorMessage(message: string) {
    LAST_ERROR_MESSAGE.value = message || ''
    try { sessionStorage.setItem('buyer_last_error', LAST_ERROR_MESSAGE.value) } catch (e) { }
  }
  function loadLastErrorMessage() {
    try { LAST_ERROR_MESSAGE.value = sessionStorage.getItem('buyer_last_error') || '' } catch (e) { LAST_ERROR_MESSAGE.value = '' }
  }
  function clearLastErrorMessage() {
    LAST_ERROR_MESSAGE.value = ''
    try { sessionStorage.removeItem('buyer_last_error') } catch (e) { }
  }

  // Управление телефоном логина: сохраняем в sessionStorage, чтобы не терялся при навигации
  function setLoginPhone(phone: string) {
    LOGIN_PHONE.value = phone || ''
    try { sessionStorage.setItem('buyer_login_phone', LOGIN_PHONE.value) } catch (e) { }
  }
  function loadLoginPhone() {
    try { LOGIN_PHONE.value = sessionStorage.getItem('buyer_login_phone') || '' } catch (e) { LOGIN_PHONE.value = '' }
  }

  function setLoginAccept(isAccepted: boolean) {
    LOGIN_ACCEPT.value = !!isAccepted
    try { sessionStorage.setItem('buyer_login_accept', LOGIN_ACCEPT.value ? '1' : '0') } catch (e) { }
  }
  function loadLoginAccept() {
    try {
      const val = sessionStorage.getItem('buyer_login_accept')
      LOGIN_ACCEPT.value = val === '1'
    } catch (e) { LOGIN_ACCEPT.value = false }
  }

  function setBuyerToken(token: string | null, update_cookie = false) {
    if (update_cookie) {
      const max_age_seconds = 31 * 24 * 60 * 60;
      document.cookie = `buyer_access_token=${token}; path=/; max-age=${max_age_seconds};`;
    }
    BUYER_TOKEN.value = token;
  }

  function setSurveyId(id: string | null) {
    SURVEY_ID.value = id || null;
    try { id ? localStorage.setItem('buyer_survey_id', id) : localStorage.removeItem('buyer_survey_id'); } catch (e) { }
  }

  function loadSurveyIdFromStorage() {
    try { SURVEY_ID.value = localStorage.getItem('buyer_survey_id'); } catch (e) { }
  }

  function setTaskDetails(details: any) {
    CURRENT_TASK_DETAILS.value = details || null
    try { 
      localStorage.setItem('buyer_task_details', JSON.stringify(details || {})); 
    } catch (e) { 
      console.error(`setTaskDetails error`, e)
    }
  }

  function loadTaskDetailsFromStorage() {
    try {
      const raw = localStorage.getItem('buyer_task_details')
      CURRENT_TASK_DETAILS.value = raw ? JSON.parse(raw) : null
    } catch (e) {
      CURRENT_TASK_DETAILS.value = null
      console.error(`loadTaskDetailsFromStorage`, e)
    }
  }

  async function postSendBuyerCredentials(data: { phone: string; task_url?: string; seller_id?: number | null; tsm_id?: number | null }) {
    return await networkController.post(apiEndpoints.buyers.auth, data)
      .then((res: any) => res?.data)
      .catch(() => ({ error_code: 500, error_message: 'Server error' }));
  }

  async function postSendBuyerCode(data: { code: string; auth_session_id: string; hash: string; task_url?: string; seller_id?: number | null; tsm_id?: number | null }) {
    return await networkController.post(apiEndpoints.buyers.auth_code, data)
      .then((res: any) => res?.data)
      .catch(() => ({ error_code: 500, error_message: 'Server error' }));
  }

  async function postBuyersStore(data: { name: string; phone: string; task_url: string; seller_id?: number | null; tsm_id?: number | null; }) {
    return await networkController.post(apiEndpoints.buyers.store, data, {
      headers: { Authorization: `Bearer ${BUYER_TOKEN.value}` }
    })
      .then((res: any) => res?.data)
      .catch(() => ({ error_code: 500, error_message: 'Server error' }));
  }

  async function postTaskDetails(data: { seller_id?: number | null; task_id: number; tsm_id?: number | null; }) {
    return await networkController.post(apiEndpoints.buyers.task_details, data, {
      headers: { Authorization: `Bearer ${BUYER_TOKEN.value}` }
    })
      .then((res: any) => res?.data)
      .catch(() => ({ error_code: 500, error_message: 'Server error' }));
  }

  async function checkAvailability(task_url: string) {
    return await networkController.post(apiEndpoints.buyers.check_availability, {task_url})
      .then((res: any) => res?.data)
      .catch(() => ({ error_code: 500, error_message: 'Server error' }));
  }

  // ====== Прохождение опроса ======
  const SURVEY_ANSWERS = ref<Array<{ question_id: number; type?: string, current_answers: Array<{ answer_id?: number | null; answer_photo?: string | null; comment: string | null; qr_data: string | null }> }>>([])
  
  function resetSurveyAnswers() {
    SURVEY_ANSWERS.value = []
  }
  
  function setAnswerForQuestion(
    questionId: number,
    payload: Array<{ answer_id?: number | null; answer_photo?: string | null; comment: string | null; qr_data: string | null;}>,
    type?: string
  ) {
    const idx = SURVEY_ANSWERS.value.findIndex(q => q.question_id === questionId)
    const normalized = (payload || []).map(a => {
      const item: {
        answer_id?: number | null
        answer_photo?: string | null
        comment: string | null
        qr_data?: string | null
      } = {
        comment: (a?.comment ?? null),
      }
      if (a && 'answer_id' in a) {
        item.answer_id = a?.answer_id ?? null
      }
      if (a && 'answer_photo' in a) {
        item.answer_photo = a?.answer_photo ?? null
        item.qr_data = a?.qr_data ?? null
      }
      return item
    })
    if (idx === -1) {
      SURVEY_ANSWERS.value.push({ question_id: questionId, type, current_answers: normalized })
    } else {
      SURVEY_ANSWERS.value[idx] = { question_id: questionId, type, current_answers: normalized }
    }
    try { sessionStorage.setItem('buyer_survey_answers', JSON.stringify(SURVEY_ANSWERS.value)) } catch (e) { }
  }
  function loadSurveyAnswers() {
    try {
      const raw = sessionStorage.getItem('buyer_survey_answers')
      SURVEY_ANSWERS.value = raw ? JSON.parse(raw) : []
    } catch (e) {
      SURVEY_ANSWERS.value = []
    }
  }

  async function completeBuyerTask(data: { task_id: number; seller_id?: number | null; tsm_id?: number | null; details: { survey: { answers: typeof SURVEY_ANSWERS.value } } }) {
    return await networkController.post(apiEndpoints.buyers.complete_task, { data }, {
      headers: { Authorization: `Bearer ${BUYER_TOKEN.value}` }
    })
      .then((res: any) => res?.data)
      .catch(() => ({ error_code: 500, error_message: 'Server error' }));
  }

  async function uploadBuyerFile(data: { task_id: number; seller_id: number | null; file: File; tsm_id?: number | null; question_id?: number | null }) {
    const formData = new FormData()
    formData.append('task_id', String(data.task_id))
    if (data.tsm_id !== null && data.tsm_id !== undefined) {
      formData.append('tsm_id', String(data.tsm_id))
    }
    if ((!data.tsm_id || data.tsm_id === null) && data.seller_id !== null && data.seller_id !== undefined) {
      formData.append('seller_id', String(data.seller_id))
    }
    if (data.question_id !== null && data.question_id !== undefined) {
      formData.append('question_id', String(data.question_id))
    }
    formData.append('file', data.file)
    return await networkController.post(
      apiEndpoints.buyers.upload_file,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${BUYER_TOKEN.value}` } }
    )
      .then((res: any) => res?.data)
      .catch(() => ({ error_code: 500, error_message: 'Server error' }))
  }

  function clearBuyerTaskData() {
    // Очищаем данные в store
    CURRENT_SELLER_ID.value = null
    CURRENT_TASK_ID.value = null
    CURRENT_TASK_DETAILS.value = null

    // Очищаем данные из localStorage
    try {
      localStorage.removeItem('buyer_seller_id')
      localStorage.removeItem('buyer_tsm_id')
      localStorage.removeItem('buyer_task_details')
    } catch (e) { 
      console.error(`clearBuyerTaskData`, e)
    }
  }
  async function getBuyerTaskRules(data: { task_url: string }) {
    return await networkController.post(apiEndpoints.buyers.task_rules, data)
      .then((res: any) => res?.data)
      .catch(() => ({ error_code: 500, error_message: 'Server error' }))
  }

  return {
    BUYER_TOKEN,
    BUYER_INFO,
    SURVEY_ID,
    LOGIN_PHONE,
    LOGIN_ACCEPT,
    CURRENT_SELLER_ID,
    CURRENT_TASK_ID,
    CURRENT_TASK_DETAILS,
    AUTH_STEP,
    setAuthStep,
    // Экран недоступности
    LAST_ERROR_MESSAGE,
    setLastErrorMessage,
    loadLastErrorMessage,
    clearLastErrorMessage,
    setLoginPhone,
    loadLoginPhone,
    setLoginAccept,
    loadLoginAccept,
    setBuyerToken,
    setSurveyId,
    loadSurveyIdFromStorage,
    setTaskDetails,
    loadTaskDetailsFromStorage,
    postSendBuyerCredentials,
    postSendBuyerCode,
    postBuyersStore,
    postTaskDetails,
    checkAvailability,
    // Опрос
    SURVEY_ANSWERS,
    resetSurveyAnswers,
    setAnswerForQuestion,
    loadSurveyAnswers,
    completeBuyerTask,
    uploadBuyerFile,
    clearBuyerTaskData,
    getBuyerTaskRules
  }
}); 